import React, { useEffect, useState } from 'react';
import { Link } from 'gatsby';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import Cookies from 'universal-cookie';

import Loader from '@youship/components/objects/loader';

import SuccessIllustration from 'images/partners/register_form_success_illustration.inline.svg';

import PersonalInformationStep from './personal-information-step';
import ConfirmationCodeStep from './confirmation-code-step';

import './styles.scss';


const cookies = new Cookies();

const WEB_APP_URL = process.env.GATSBY_WEB_APP_URL;

const steps = [PersonalInformationStep, ConfirmationCodeStep];
const PERSONAL_INFORMATION_TITLE = 'partners.form.title_personal';
const PHONE_ACTIVATION_TITLE = 'partners.form.title_activation';
//

const PartnerRegistrationForm = ({ classNames }) => {
  const [isUserAuthenticated, setUserAuthenticated] = useState(!!cookies.get('AUTH_TOKEN'));
  const [userRequiresPhoneActivation, setUserRequiresPhoneActivation] = useState(cookies.get('USER_REQUIRES_PHONE_ACTIVATION') === 'true');
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [succeeded, setSucceeded] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(
    () => {
      if (isUserAuthenticated && userRequiresPhoneActivation && currentStep !== 2) {
        setCurrentStep(2);
      }
    },
    // currentStep is not required for this hook
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isUserAuthenticated, userRequiresPhoneActivation]
  );

  const handleStepSuccess = (message) => {
    window.scrollTo(0, 0);
    setIsSubmitting(false);

    if (currentStep !== steps.length) {
      setCurrentStep(prevState => prevState + 1);
    } else {
      setSucceeded(true);
      setUserAuthenticated(!!cookies.get('AUTH_TOKEN'));
      setUserRequiresPhoneActivation(cookies.get('USER_REQUIRES_PHONE_ACTIVATION') === 'true');
      window.location = `${WEB_APP_URL}/account-confirmation/partner`;

      if (message) {
        setSuccessMessage(message);
      }
    }
  };

  const handleStepError = () => {
    setIsSubmitting(false);
  };

  const Step = steps[currentStep - 1];

  let formSubTitle = null;

  switch (currentStep) {
    case 1:
      formSubTitle = PERSONAL_INFORMATION_TITLE;
      break;
    case 2:
      formSubTitle = PHONE_ACTIVATION_TITLE;
      break;
    default:
      break;
  }

  return (
    <div className={`partner-registration-form${classNames ? ` ${classNames}` : ''} ${succeeded ? ' partner-registration-form--success' : ''}`}>
      {isUserAuthenticated && !userRequiresPhoneActivation ? (
        <div className="partner-registration-form__success">
          <SuccessIllustration
            alt="Form success"
            className="partner-registration-form__success-image"
          />
          <div className="partner-registration-form__success-text">
            <div className="partner-registration-form__success-title">
              <FormattedMessage id="partners.form.success_title" />
            </div>
            <div className="partner-registration-form__success-wrapper">
              <span className="partner-registration-form__success-description">
                <FormattedMessage id="partners.form.authenticated_signin_title" />
              </span>
              <Link
                className="partner-registration-form__footer-link"
                to={`${WEB_APP_URL}/account-confirmation/partner`}
              >
                {' '}
                <FormattedMessage id="partners.form.authenticated_signin" />
              </Link>
            </div>
          </div>
        </div>
      ) : (
        <>
          {isSubmitting && (
            <div className="partner-registration-form__loader-wrapper">
              <Loader />
            </div>
          )}
          <div className="partner-registration-form__header">
            <div className="partner-registration-form__title">
              <FormattedMessage id="partners.form.title" />
            </div>
            <div className="partner-registration-form__steps">
              <FormattedMessage id="partners.form.step" />
              {' '}
              {(isUserAuthenticated && userRequiresPhoneActivation) ? steps.length : currentStep}
              /
              {steps.length}
            </div>
          </div>
          {formSubTitle && (
            <div className="partner-registration-form__subtitle">
              <FormattedMessage id={formSubTitle} />
            </div>
          )}
          <Step
            classNames={succeeded ? ' partner-registration-form__step--hide' : ''}
            successMessage={currentStep === 2 ? successMessage : null}
            onError={handleStepError}
            onSubmitting={() => setIsSubmitting(true)}
            onSuccess={handleStepSuccess}
          />
          {currentStep === 1 && (
            <>
              <hr className="partner-registration-form__separator" />
              <div className="partner-registration-form__footer">
                <span className="partner-registration-form__footer-text">
                  <FormattedMessage id="partners.form.already_started" />
                </span>
                <Link
                  className="partner-registration-form__footer-link"
                  to={`${WEB_APP_URL}/account-confirmation/partner`}
                >
                  {' '}
                  <FormattedMessage id="partners.form.finish" />
                </Link>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

PartnerRegistrationForm.propTypes = {
  classNames: PropTypes.string
};

PartnerRegistrationForm.defaultProps = {
  classNames: null
};

export default PartnerRegistrationForm;
